import { Box, CircularProgress, Typography } from "@mui/material";
import { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CalculatorTabs } from "../components/calculator/CalculatorTabs";
import { ProviderSupplies } from "../components/calculator/ProvidersCleanner/components/ProvidersSupplies";
import { ClientCreate } from "../components/clients/ClientCreate";
import { ClientsTable } from "../components/clients/clientsTable";
import { ConnectBusinessAccount } from "../components/ConnectBusinessAccount";
import { ConnectBusinessAlgorithm } from "../components/connectBusinessAlgorithm";
import { ConnectBusinessCoverageArea } from "../components/connectBusinessCoverageArea";
import { ConnectionRequests } from "../components/connectionRequests";
import { WorkforceDetailTabs } from "../components/employees/components/WorkforceDetailTabs";
import { WorkforceList } from "../components/employees/components/WorkforceList";
import { HoursOfOperation } from "../components/hoursOfOperation";
import { InvoiceCreateView } from "../components/invoice/create/InvoiceCreateView";
import JobsList from "../components/jobs/jobs-list/JobsList";
//import { JobsCalendar } from "../components/jobs2/jobs-calendar/JobsCalendar";
import { NumbersConfiguration } from "@components/virtual-receptionist/widget/components/NumbersConfiguration";
import { ManageAdmins } from "../components/admins/ManageAdmins";
import { MinimizedModalsTabs } from "../components/common/MinimizedModalsTabs";
import { HelpSupportMessage } from "../components/common/SidebarToolkit/HelpContent";
import { InvoiceDetailsByUid } from "../components/invoice/view/components/DetailsView/InvoiceDetailsByUid";
import { JobDetails } from "../components/jobs/jobs-details/JobDetails";
import { useJobsByIdQuery } from "../components/jobs/jobs-list/jobs-layout/hooks/useJobByIdQuery";
import { JobLayout } from "../components/jobs/jobs-list/jobs-layout/Job.layout";
import { CalendarJobsCustom } from "../components/jobs2/CalendarJobsCustom";
import { JobsCreate } from "../components/jobs2/JobCreate/Jobs.layout";
import { EventJobModal } from "../components/jobs2/jobs-calendar/components/calendar-container/modal/components/components/EventJobModal";
import { useJobEventsByIdQuery } from "../components/jobs2/jobs-calendar/hooks/useJobEventsByIdQuery";
import { buildCalendarEventArg } from "../components/jobs2/jobs-calendar/utils/buildCalendarEventArg";
import { mappedDataForCalendar } from "../components/jobs2/jobs-calendar/utils/mappedDataForCalendar";
import VaultContent from "../components/password-baul/component/principals/VaultContent";
import PrivacyPolicy from "../components/privacy-policy";
import { QuoteCreation } from "../components/quotes/quote-create-stepper/QuoteCreation";
import { QuoteView } from "../components/quotes/quote-detail/view/QuoteView";
import { QuoteTable } from "../components/quotes/QuotesTable/QuoteTable";
import { RecordFormModal } from "../components/sector-workflow-editor/components/RecordFromModal";
import { ManageServices } from "../components/services/ManageServices";
import { CompanyTimezoneModalContainer } from "../components/shared/components/features/event-scheduler/components/timezone/TimeZoneConfiguration";
import { TermsAndConditions } from "../components/termsAndConditions";
import { useDialog } from "../components/ui/dialogs/useDialog";
import { WorkforceCreateView } from "../components/workforce/views/WorkforceCreateView";
import { selectActiveCompanyId } from "../redux/features/user/selectors/userState.selectors";
import { clearModalDockStorage } from "../utils/modalDockStorage";

const LazyInvoicesTable = lazy(() =>
  import("../components/invoice/DashboardWidget/InvoicesTable").then((module) => ({
    default: module.InvoicesTable,
  })),
);

const RestoredInvoicesTable = () => (
  <Suspense
    fallback={
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
        <CircularProgress size={24} />
      </Box>
    }
  >
    <LazyInvoicesTable />
  </Suspense>
);

const RestoredJobDetails = ({ jobId, modalKey }) => {
  const { data: job, isLoading, isError } = useJobsByIdQuery({ idStr: jobId });

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError || !job) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={240} px={3}>
        <Typography color="text.secondary" fontSize={14}>
          Unable to load this job.
        </Typography>
      </Box>
    );
  }

  return <JobDetails job={job} backModalKey={modalKey} />;
};

const RestoredCalendarEventModal = ({ jobId, eventUid }) => {
  const { events, isLoading, isError } = useJobEventsByIdQuery(jobId);

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const event = events.find((item) => String(item?.uid) === String(eventUid)) || events[0];
  const calendarEvent = mappedDataForCalendar(event);

  if (isError || !calendarEvent) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={240} px={3}>
        <Typography color="text.secondary" fontSize={14}>
          Unable to load this visit.
        </Typography>
      </Box>
    );
  }

  return <EventJobModal arg={buildCalendarEventArg(calendarEvent)} />;
};

const MODAL_DOCK_STORAGE_KEY = "flownect:minimized-modal-dock:v1";

const getModalDockStorageKey = (companyId) => {
  if (companyId == null || companyId === "") return null;
  return `${MODAL_DOCK_STORAGE_KEY}:company:${companyId}`;
};

const getPrimitiveProps = (props = {}) =>
  Object.entries(props).reduce((primitiveProps, [key, value]) => {
    if (["string", "number", "boolean"].includes(typeof value) || value === null) {
      primitiveProps[key] = value;
    }

    return primitiveProps;
  }, {});

const getModalDockRestoreState = (modal) => {
  if (!modal?.key) return null;

  return {
    key: modal.key,
    title: modal.title,
    label: modal.label,
    restoreProps: getPrimitiveProps(modal.content?.props),
  };
};

const getStoredDockItems = (storageKey) => {
  if (typeof window === "undefined") return [];
  if (!storageKey) return [];

  try {
    const rawDock = window.localStorage.getItem(storageKey);
    if (!rawDock) return [];

    const dockItems = JSON.parse(rawDock);
    if (!Array.isArray(dockItems)) return [];

    return dockItems.filter((item) => item?.key);
  } catch (error) {
    window.localStorage.removeItem(storageKey);
    return [];
  }
};

const setStoredDockItems = (storageKey, dockItems) => {
  if (typeof window === "undefined") return;
  if (!storageKey) return;

  if (!dockItems.length) {
    window.localStorage.removeItem(storageKey);
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify(dockItems));
};

const getRestorableModalContent = (modalKey, modalContentMap, restoreProps = {}) => {
  const staticContent = modalContentMap[modalKey];
  if (staticContent) return staticContent;

  const quoteViewMatch = String(modalKey).match(/^QuoteRow-ViewQuote-(\d+)(?:-.+)?$/);
  if (quoteViewMatch) {
    return <QuoteView quoteId={Number(quoteViewMatch[1])} />;
  }

  const invoiceDetailsMatch = String(modalKey).match(/^Dashboard-Invoice-Details(?:-(.+))?$/);
  if (invoiceDetailsMatch) {
    const invoiceUid = restoreProps.invoiceUid || invoiceDetailsMatch[1];
    if (invoiceUid) {
      return <InvoiceDetailsByUid invoiceUid={invoiceUid} scopeOfWorkUid={restoreProps.scopeOfWorkUid} />;
    }
  }

  const jobCardMatch = String(modalKey).match(/(?:^|-)JobCard-(\d+)$/);
  if (jobCardMatch) {
    return <RestoredJobDetails jobId={Number(jobCardMatch[1])} modalKey={modalKey} />;
  }

  const workforcePortfolioMatch = String(modalKey).match(/^Workforce-Portfolio-Details-(.+)$/);
  if (workforcePortfolioMatch) {
    return <WorkforceDetailTabs workforceUid={workforcePortfolioMatch[1]} />;
  }

  const calendarVisitMatch = String(modalKey).match(/^Calendar-Visit-Stepper-(\d+)-(.+)$/);
  if (calendarVisitMatch) {
    const eventUid = calendarVisitMatch[2] === "event" ? undefined : calendarVisitMatch[2];
    return <JobLayout parentId={Number(calendarVisitMatch[1])} currentEventUid={eventUid} showCalendar={false} />;
  }

  const calendarEventMatch = String(modalKey).match(/^(?:Jobs-Calendar|Grouped-Event)-(\d+)-(.+)$/);
  if (calendarEventMatch) {
    return <RestoredCalendarEventModal jobId={Number(calendarEventMatch[1])} eventUid={calendarEventMatch[2]} />;
  }

  const notificationJobMatch = String(modalKey).match(/^(?:Notification|NotificationBell|NotificationsCenter)-Job-(\d+)$/);
  if (notificationJobMatch) {
    return <JobLayout parentId={Number(notificationJobMatch[1])} />;
  }

  const notificationQuoteMatch = String(modalKey).match(/^(?:Notification|NotificationBell|NotificationsCenter)-Quote-(\d+)$/);
  if (notificationQuoteMatch) {
    return <QuoteView quoteId={Number(notificationQuoteMatch[1])} />;
  }

  const notificationInvoiceMatch = String(modalKey).match(/^(?:Notification|NotificationBell|NotificationsCenter)-Invoice-(.+)$/);
  if (notificationInvoiceMatch) {
    return <InvoiceDetailsByUid invoiceUid={notificationInvoiceMatch[1]} />;
  }

  if (/^(?:Notification|NotificationBell|NotificationsCenter)-ManageAdmins$/.test(String(modalKey))) {
    return <ManageAdmins />;
  }

  return null;
};

const getNextModalZIndex = (modals = [], fallback = 30000) => {
  const highestZIndex = modals.reduce((highest, modal) => Math.max(highest, Number(modal?.zIndex) || 0), fallback);
  return highestZIndex + 2;
};

const getRestorePriority = (modalKey) => {
  const key = String(modalKey);

  if (key === "Dashboard-Jobs-Calendar" || key === "Dashboard-JobsCalendar-Business") return 10;
  if (/^(?:Jobs-Calendar|Grouped-Event)-\d+-.+/.test(key)) return 20;
  if (/^CalendarEvent-JobCard-\d+/.test(key)) return 30;
  if (/^Calendar-Visit-Stepper-\d+-.+/.test(key)) return 40;

  return 25;
};

const normalizeRestoreModalKeys = (modalKeys = []) =>
  Array.from(new Set(modalKeys.filter(Boolean))).sort((a, b) => {
    const priorityDiff = getRestorePriority(a) - getRestorePriority(b);
    if (priorityDiff !== 0) return priorityDiff;

    return modalKeys.indexOf(a) - modalKeys.indexOf(b);
  });

const modalCascadeCloseMap = {
  "Dashboard-Invoices-See": ["Dashboard-Invoice-Details"],
};

const shouldCloseModalByCascade = (modal, targetKey) => {
  if (modal?.key === targetKey) return true;

  if (targetKey === "Dashboard-Invoices-See" && String(modal?.key).startsWith("Dashboard-Invoice-Details")) {
    return !modal?.minimized;
  }

  const childKeys = new Set(modalCascadeCloseMap[targetKey] ?? []);
  return childKeys.has(modal?.key) && !modal?.minimized;
};

const clearOpenCascadeChildren = (modals, targetKey) => {
  const childKeys = new Set(modalCascadeCloseMap[targetKey] ?? []);
  if (childKeys.size === 0) return modals;

  return modals.filter((modal) => {
    const isInvoiceDetailsChild = targetKey === "Dashboard-Invoices-See" && String(modal?.key).startsWith("Dashboard-Invoice-Details");
    return (!childKeys.has(modal?.key) && !isInvoiceDetailsChild) || modal?.minimized;
  });
};

const ModalContext = createContext();

export const useModals = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  // Mapa de contenido predeterminado para modales
  const modalContentMap = {
    "Privacy-policy": <PrivacyPolicy />,
    "Dashboard-Account-Settings": <ConnectBusinessAccount />,
    "Dashboard-Clients-Business": <ClientsTable disableInnerChilds={true} />,
    "Dashboard-ClientAdd-Business": <ClientCreate />,
    "Dashboard-Jobs-Business": <JobsList />,
    "Dashboard-QuoteAdd-Business": <QuoteCreation />,
    "Dashboard-QuotesAdd-Business": <QuoteCreation />,
    "Dashboard-Quotes-Business": <QuoteTable />,
    "Dashboard-Invoices-See": <RestoredInvoicesTable />,
    "Dashboard-Invoices-Create": <InvoiceCreateView />,
    "Dashboard-JobsCalendar-Business": <CalendarJobsCustom />,
    "Dashboard-Jobs-Calendar": <CalendarJobsCustom restoreModalKey="Dashboard-Jobs-Calendar" />,
    "Dashboard-Connections-Business": <ConnectionRequests />,
    "Dashboard-FindAExpert-Business": <ConnectBusinessAlgorithm />,
    "Dashboard-Providers-Business": <ProviderSupplies />,
    "Dashboard-Calculator-Business": <CalculatorTabs />,
    "Organization-CoverageAreas-Business": <ConnectBusinessCoverageArea />,
    "Organization-ManageServices-Business": <ManageServices />,
    "Organization-ManageHours-Business": <HoursOfOperation />,
    "Organization-PasswordVault-Business": <VaultContent />,
    "Dashboard-Workforces-List": <WorkforceList />,
    "Dashboard-Workforce-Create": <WorkforceCreateView />,
    "Dashboard-InvoiceCreate-Business": <InvoiceCreateView />,
    "Superadmin-SectorWorkflows-Settings": <RecordFormModal />,
    "Organization-TimeZone-Business": <CompanyTimezoneModalContainer />,
    "Terms-Service-Modal": <TermsAndConditions openInNewPage={false} setOpenTermsAndConditions={() => {}} />,
    "Help-Modal": <HelpSupportMessage emailDirection={"contact@flownect.com"} />,
    "Organization-PhoneNumbers": <NumbersConfiguration />,
    "Dashboard-JobsAdd-Business-Without-Quote-all": <JobsCreate withQuoteService={false} clientUidSelected={null} />,
  };

  const [modals, setModals] = useState([]);
  const [nextZIndex, setNextZIndex] = useState(30000);
  const nextZIndexRef = useRef(30000);
  const [expandItem, setExpandItem] = useState(true);
  const [modalRendererCounts, setModalRendererCounts] = useState({});
  const [hasHydratedDock, setHasHydratedDock] = useState(false);
  const [activeDockStorageKey, setActiveDockStorageKey] = useState(null);
  const activeDockStorageKeyRef = useRef(null);
  const skipNextDockPersistRef = useRef(false);
  const savedDockBeforeCompanySwitchRef = useRef(false);
  const modalRegistryRef = useRef(new Map());
  const { showDialog, DialogComponent } = useDialog();

  const location = useLocation();
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const shouldShowModalDock = !location.pathname.startsWith("/releases");
  const hasAuthToken = Boolean(typeof window !== "undefined" && (window.sessionStorage.getItem("token") || window.localStorage.getItem("token")));
  const dockStorageKey = useMemo(() => getModalDockStorageKey(activeCompanyId), [activeCompanyId]);

  const handleExpand = useCallback((expand) => {
    setExpandItem(!expand);
  }, []);

  const openModal = useCallback((modalData, customContent = null, onConfirmCloseCallback = null) => {
    setModals((current) => {
      const modalKey = typeof modalData === "string" ? modalData : modalData.key;
      const callbackFromData = typeof modalData === "object" ? modalData.onConfirmClose : null;
      const finalCallback = onConfirmCloseCallback || callbackFromData;

      const restoreProps = typeof modalData === "object" ? modalData.restoreProps : undefined;
      const mappedContent = getRestorableModalContent(modalKey, modalContentMap, restoreProps);
      const content = customContent || mappedContent;

      if (!content) {
        console.warn(`⚠️ No content found for modalKey: ${modalKey}`);
        return current;
      }

      const isJobCard = modalKey.includes("JobCard-");
      const isWorkForceCard = modalKey.startsWith("WorkForceCard-");
      const isManagedProviderSupplie = modalKey.startsWith("Manage-supplies-");
      const isEditProviderSupplie = modalKey.startsWith("edit-provider-");
      const isViewQuote = modalKey.startsWith("QuoteRow-ViewQuote-");
      const isInvoicePreview = modalKey.startsWith("InvoicePreview-");
      const isInvoiceCreate = modalKey.startsWith("Dashboard-InvoiceCreate-Business");

      const preventParentTrigger = typeof modalData === "object" ? modalData.preventParentTrigger : false;
      const currentWithoutOpenChildren = clearOpenCascadeChildren(current, modalKey);
      const modalZIndex = getNextModalZIndex(currentWithoutOpenChildren, nextZIndexRef.current);

      const newModal = {
        id: Date.now(),
        key: modalKey,
        content,
        title: typeof modalData === "object" ? modalData.title || modalData.label : undefined,
        label: typeof modalData === "object" ? modalData.label : undefined,
        minimized: false,
        zIndex: modalZIndex,
        onConfirmClose: finalCallback,
        preventParentTrigger,
      };

      nextZIndexRef.current = Math.max(nextZIndexRef.current, modalZIndex + 1);
      setNextZIndex((prev) => Math.max(prev, modalZIndex + 1));

      if (isViewQuote) {
        const filtered = currentWithoutOpenChildren.filter((m) => m.key !== modalKey && (!m.key.startsWith("QuoteRow-ViewQuote") || m.minimized));
        return [...filtered, newModal];
      }

      if (isInvoicePreview) {
        const filtered = currentWithoutOpenChildren.filter((m) => !m.key.startsWith("InvoicePreview-"));
        return [...filtered, newModal];
      }

      if (isJobCard) {
        const filtered = currentWithoutOpenChildren.filter((m) => m.key !== modalKey && (!m.key.includes("JobCard-") || m.minimized));
        return [...filtered, newModal];
      }

      if (isWorkForceCard) {
        const filtered = currentWithoutOpenChildren.filter((m) => !m.key.startsWith("WorkForceCard-"));
        return [...filtered, newModal];
      }

      if (isManagedProviderSupplie) {
        const filtered = currentWithoutOpenChildren.filter((m) => !m.key.startsWith("Manage-supplies-"));
        return [...filtered, newModal];
      }

      if (isEditProviderSupplie) {
        const filtered = currentWithoutOpenChildren.filter((m) => !m.key.startsWith("edit-provider-"));
        return [...filtered, newModal];
      }

      if (isInvoiceCreate) {
        const filtered = currentWithoutOpenChildren.filter((m) => !m.key.startsWith("Dashboard-InvoiceCreate-Business"));
        return [...filtered, newModal];
      }

      const isRelatedDepositInvoice =
        modalKey === "RelatedDepositInvoice" || (typeof modalKey === "string" && modalKey.startsWith("RelatedDepositInvoice-"));
      if (isRelatedDepositInvoice) {
        const filtered = currentWithoutOpenChildren.filter((m) => !String(m.key).startsWith("RelatedDepositInvoice"));
        return [...filtered, newModal];
      }

      // Same key already in stack: refresh content, un-minimize, bump z-index (do not no-op).
      // Also drop duplicate keys so BaseModal only ever has one entry per modalKey.
      const existing = currentWithoutOpenChildren.find((m) => m.key === modalKey);
      if (existing) {
        const withoutDupes = currentWithoutOpenChildren.filter((m) => m.key !== modalKey);
        return [
          ...withoutDupes,
          {
            ...existing,
            content,
            title: typeof modalData === "object" ? modalData.title || modalData.label || existing.title : existing.title,
            label: typeof modalData === "object" ? modalData.label || existing.label : existing.label,
            minimized: false,
            zIndex: modalZIndex,
            onConfirmClose: finalCallback ?? existing.onConfirmClose,
            preventParentTrigger,
          },
        ];
      }

      return [...currentWithoutOpenChildren, newModal];
    });
  }, []);

  const closeModal = useCallback((id) => {
    setModals((current) => {
      const target = current?.find((m) => m.id === id);
      if (!target?.key) return current?.filter((m) => m.id !== id);

      return current?.filter((m) => !shouldCloseModalByCascade(m, target.key));
    });
  }, []);

  const minimizeModal = useCallback((id) => {
    setModals((current) => current?.map((m) => (m.id === id ? { ...m, minimized: true } : m)));
  }, []);

  const restoreModal = useCallback((id) => {
    setModals((current) => current?.map((m) => (m.id === id ? { ...m, minimized: false } : m)));
  }, []);

  const reorderModal = useCallback((sourceId, targetId) => {
    if (sourceId === targetId) return;

    setModals((current) => {
      const sourceIndex = current.findIndex((modal) => String(modal.id) === String(sourceId));
      const targetIndex = current.findIndex((modal) => String(modal.id) === String(targetId));

      if (sourceIndex === -1 || targetIndex === -1) return current;

      const next = [...current];
      const [sourceModal] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, sourceModal);
      return next;
    });
  }, []);

  const closeAllModals = useCallback(() => {
    setModals([]);
  }, []);

  const prepareModalDockForCompanySwitch = useCallback(() => {
    const storageKey = activeDockStorageKeyRef.current;

    setModals((current) => {
      if (storageKey) {
        const dockItems = current
          .filter((modal) => modal?.minimized)
          .map(getModalDockRestoreState)
          .filter(Boolean);
        setStoredDockItems(storageKey, dockItems);
      }

      skipNextDockPersistRef.current = true;
      savedDockBeforeCompanySwitchRef.current = true;
      return [];
    });
  }, []);

  const closeAllModalsStartWith = useCallback((prefix) => {
    setModals((current) => current.filter((m) => !m.key.startsWith(prefix)));
  }, []);

  const bringToFront = useCallback(
    (id) => {
      setModals((current) => {
        const modalIndex = current.findIndex((m) => m.id === id);
        if (modalIndex === -1) return current;

        const updatedModals = [...current];
        const modalZIndex = getNextModalZIndex(current, nextZIndex);
        updatedModals[modalIndex] = {
          ...updatedModals[modalIndex],
          zIndex: modalZIndex,
        };
        setNextZIndex((prev) => Math.max(prev, modalZIndex + 1));
        return updatedModals;
      });
    },
    [nextZIndex],
  );

  const closeByKey = useCallback((key) => {
    setModals((current) => {
      return current.filter((m) => !shouldCloseModalByCascade(m, key));
    });
  }, []);

  const registerModal = useCallback((modalKey, config) => {
    if (!modalKey) return () => {};

    modalRegistryRef.current.set(modalKey, config);

    return () => {
      modalRegistryRef.current.delete(modalKey);
    };
  }, []);

  const getModalConfig = useCallback((modalKey) => {
    if (!modalKey) return null;
    return modalRegistryRef.current.get(modalKey) || null;
  }, []);

  const registerModalRenderer = useCallback((modalKey) => {
    if (!modalKey) return () => {};

    setModalRendererCounts((current) => ({
      ...current,
      [modalKey]: (current[modalKey] || 0) + 1,
    }));

    return () => {
      setModalRendererCounts((current) => {
        const nextCount = (current[modalKey] || 0) - 1;
        if (nextCount > 0) {
          return {
            ...current,
            [modalKey]: nextCount,
          };
        }

        const next = { ...current };
        delete next[modalKey];
        return next;
      });
    };
  }, []);

  const restoreModalStack = useCallback(
    (modalKeys = []) => {
      const keys = normalizeRestoreModalKeys(modalKeys);
      const maxAttempts = 30;
      const retryDelayMs = 200;
      const nextLayerDelayMs = 250;

      const restoreLayer = (index) => {
        if (index >= keys.length) return;

        const modalKey = keys[index];
        let attempts = 0;

        const tryOpen = () => {
          const registeredModal = modalRegistryRef.current.get(modalKey);
          if (registeredModal?.content) {
            openModal(
              {
                key: modalKey,
                preventParentTrigger: registeredModal.preventParentTrigger,
              },
              registeredModal.content,
              registeredModal.onConfirmClose,
            );
            setTimeout(() => restoreLayer(index + 1), nextLayerDelayMs);
            return;
          }

          const mappedContent = getRestorableModalContent(modalKey, modalContentMap);

          if (mappedContent) {
            openModal(modalKey, mappedContent);
            setTimeout(() => restoreLayer(index + 1), nextLayerDelayMs);
            return;
          }

          attempts += 1;
          if (attempts < maxAttempts) {
            setTimeout(tryOpen, retryDelayMs);
            return;
          }

          restoreLayer(index + 1);
        };

        tryOpen();
      };

      restoreLayer(0);
    },
    [openModal],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("modals")) return;

    params.delete("modals");
    const queryString = params.toString();
    const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ""}${location.hash}`;

    window.history.replaceState(window.history.state, "", newUrl);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!hasAuthToken) {
      clearModalDockStorage();
      setModals([]);
      setActiveDockStorageKey(null);
      activeDockStorageKeyRef.current = null;
      setHasHydratedDock(false);
      return;
    }

    const previousStorageKey = activeDockStorageKeyRef.current;

    setModals((current) => {
      if (previousStorageKey && previousStorageKey !== dockStorageKey) {
        if (savedDockBeforeCompanySwitchRef.current) {
          savedDockBeforeCompanySwitchRef.current = false;
        } else {
          const previousDockItems = current
            .filter((modal) => modal?.minimized)
            .map(getModalDockRestoreState)
            .filter(Boolean);
          setStoredDockItems(previousStorageKey, previousDockItems);
        }
      }

      if (!dockStorageKey) return [];

      let storedDockItems = getStoredDockItems(dockStorageKey);
      if (!storedDockItems.length) {
        const legacyDockItems = getStoredDockItems(MODAL_DOCK_STORAGE_KEY);
        if (legacyDockItems.length) {
          storedDockItems = legacyDockItems;
          setStoredDockItems(dockStorageKey, legacyDockItems);
          window.localStorage.removeItem(MODAL_DOCK_STORAGE_KEY);
        }
      }

      const restoredModals = storedDockItems
        .map((item, index) => {
          const content = getRestorableModalContent(item.key, modalContentMap, item.restoreProps);
          if (!content) return null;

          return {
            id: `persisted-dock-${Date.now()}-${index}`,
            key: item.key,
            content,
            title: item.title,
            label: item.label,
            minimized: true,
            zIndex: 30000 + index,
            restoreProps: item.restoreProps,
          };
        })
        .filter(Boolean);

      setNextZIndex((prev) => Math.max(prev, 30000 + restoredModals.length + 2));
      return restoredModals;
    });

    activeDockStorageKeyRef.current = dockStorageKey;
    setActiveDockStorageKey(dockStorageKey);
    setHasHydratedDock(true);
  }, [dockStorageKey, hasAuthToken, location.pathname]);

  const minimizedModalTabs = useMemo(
    () =>
      modals
        .filter((modal) => modal?.minimized)
        .map((modal) => ({
          id: modal.id,
          key: modal.key,
          title: modal.title,
          label: modal.label,
          minimized: modal.minimized,
        })),
    [modals],
  );

  const minimizedDockItems = useMemo(
    () =>
      modals
        .filter((modal) => modal?.minimized)
        .map(getModalDockRestoreState)
        .filter(Boolean),
    [modals],
  );

  useEffect(() => {
    if (!hasHydratedDock || !activeDockStorageKey) return;
    if (skipNextDockPersistRef.current) {
      skipNextDockPersistRef.current = false;
      return;
    }

    setStoredDockItems(activeDockStorageKey, minimizedDockItems);
  }, [activeDockStorageKey, hasHydratedDock, minimizedDockItems]);

  return (
    <ModalContext.Provider
      value={{
        modals,
        closeAllModals,
        prepareModalDockForCompanySwitch,
        closeAllModalsStartWith,
        expandItem,
        handleExpand,
        openModal,
        closeModal,
        minimizeModal,
        restoreModal,
        reorderModal,
        restoreModalStack,
        registerModal,
        getModalConfig,
        registerModalRenderer,
        modalRendererCounts,
        bringToFront,
        closeByKey,
        showDialog,
      }}
    >
      {children}
      {hasAuthToken && shouldShowModalDock && (
        <MinimizedModalsTabs
          modals={minimizedModalTabs}
          expandItem={expandItem}
          closeModal={closeModal}
          restoreModal={restoreModal}
          reorderModal={reorderModal}
          bringToFront={bringToFront}
        />
      )}
      {DialogComponent}
    </ModalContext.Provider>
  );
};
